'use client'

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, MotionValue } from 'framer-motion';

interface ResizablePanelProps {
  children: React.ReactNode;
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  side?: 'left' | 'right';
  className?: string;
  motionValue?: MotionValue<number>;
}

export function ResizablePanel({
  children,
  defaultSize = 400,
  minSize = 200,
  maxSize = 800,
  side = 'left',
  className = '',
  motionValue,
}: ResizablePanelProps) {
  const width = useMotionValue(defaultSize);

  useEffect(() => {
    if (motionValue) {
      // Sync initial value
      width.set(motionValue.get());

      // Subscribe to external changes
      const unsubscribe = motionValue.on('change', (latest) => {
        width.set(latest);
      });

      // Subscribe to local changes
      const unsubscribeWidth = width.on('change', (latest) => {
        motionValue.set(latest);
      });

      return () => {
        unsubscribe();
        unsubscribeWidth();
      };
    }
  }, [motionValue, width]);

  const dragRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsDragging(false);
  }, []);

  const resize = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      let newSize;
      if (side === 'left') {
        newSize = window.innerWidth - e.clientX;
      } else {
        newSize = e.clientX;
      }
      
      const clampedSize = Math.min(Math.max(newSize, minSize), maxSize);
      width.set(clampedSize);
    },
    [isDragging, maxSize, minSize, side, width]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', resize, { passive: true });
      document.addEventListener('mouseup', stopResizing);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResizing);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, resize, stopResizing]);

  return (
    <motion.div
      style={{ width }}
      className={className}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
    >
      <div
        ref={dragRef}
        className={`absolute ${
          side === 'left' ? 'left-0' : 'right-0'
        } top-0 bottom-0 w-1 cursor-ew-resize hover:bg-primary/10 active:bg-primary/20 transition-colors`}
        onMouseDown={startResizing}
      />
      {children}
    </motion.div>
  );
}
